import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { clientService } from '../services/clientService';
import { assessmentService } from '../services/assessmentService';
import { activityService, Activity } from '../services/activityService';
import { useAuth } from '../contexts/AuthContext';
import { useHeader } from '../contexts/HeaderContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';
import { useEffect } from 'react';

const ROLLUP_WINDOW_MS = 10 * 60 * 1000;
const ROLLUP_ACTIVITY_TYPES = new Set(['response_created', 'response_updated', 'response_flags_updated']);

const getActivityTime = (activity: Activity): number => new Date(activity.timestamp).getTime();

const getRollupKey = (activity: Activity): string | null => {
  const activityType =
    typeof activity.metadata.activityType === 'string'
      ? activity.metadata.activityType
      : activity.type;

  if (!ROLLUP_ACTIVITY_TYPES.has(activityType)) {
    return null;
  }

  const { userId, assessmentId } = activity.metadata;
  if (typeof userId !== 'number' || typeof assessmentId !== 'number') {
    return null;
  }

  return `${activityType}:${userId}:${assessmentId}`;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { setTitle } = useHeader();

  useEffect(() => {
    const role = user?.role;
    let dashboardTitle = 'Dashboard';
    if (role === 'admin' || role === 'super_admin' || role === 'SUPER_ADMIN' || role === 'ADMIN') {
      dashboardTitle = 'Admin Dashboard';
    } else if (role === 'advisor' || role === 'ADVISOR') {
      dashboardTitle = 'Advisor Dashboard';
    } else if (role === 'client' || role === 'CLIENT_ADMIN' || role === 'CLIENT_USER') {
      dashboardTitle = 'Client Dashboard';
    }
    setTitle(dashboardTitle);
  }, [user, setTitle]);

  // Restrict only standard client users, NOT client admins
  const isRestrictedClient = user?.role === 'client' || user?.role === 'CLIENT_USER';

  const { data: clientsData, isLoading: clientsLoading } = useQuery(
    'clients-dashboard',
    () => clientService.getClients({ limit: 5 }),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      enabled: !isRestrictedClient, // Disable for restricted client users
    }
  );

  const { data: assessmentStats, isLoading: statsLoading } = useQuery(
    'assessment-stats',
    () => assessmentService.getStats(),
    {
      refetchInterval: 30000,
    }
  );

  const { data: clientUsersStats, isLoading: usersStatsLoading } = useQuery(
    'client-users-stats',
    () => clientService.getClientUsersStats(),
    {
      refetchInterval: 30000,
      enabled: !isRestrictedClient, // Disable for restricted client users
    }
  );

  const { data: activities, isLoading: activitiesLoading } = useQuery(
    'recent-activities',
    () => activityService.getRecentActivities(100),
    {
      refetchInterval: 30000,
    }
  );

  const stats = [
    {
      name: 'Total Clients',
      value: clientsData?.pagination.total || 0,
      icon: BuildingOfficeIcon,
      color: 'bg-primary-500',
      href: '/clients',
    },
    {
      name: 'Client Users',
      value: clientUsersStats?.totalClientUsers || 0,
      icon: UserGroupIcon,
      color: 'bg-warning-500',
      href: '/users',
    },
    {
      name: 'Active Evaluations',
      value: assessmentStats?.activeAssessments || 0,
      icon: ClipboardDocumentListIcon,
      color: 'bg-success-500',
      href: '/assessments?status=IN_PROGRESS,IN_REVIEW',
    },
    {
      name: 'Completed This Month',
      value: assessmentStats?.completedThisMonth || 0,
      icon: ChartBarIcon,
      color: 'bg-accent-500',
      href: '/assessments?status=COMPLETED',
    },
  ];

  // Filter out client-related stats for restricted client users
  const visibleStats = stats.filter(stat => {
    if (isRestrictedClient) {
      return stat.name !== 'Total Clients' && stat.name !== 'Client Users';
    }
    return true;
  });

  // Map activity types to icons
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'assessment_created':
      case 'assessment_started':
        return ClipboardDocumentListIcon;
      case 'assessment_completed':
        return CheckCircleIcon;
      case 'assessment_updated':
      case 'response_updated':
        return ClipboardDocumentListIcon;
      case 'client_registered':
        return BuildingOfficeIcon;
      default:
        return ClipboardDocumentListIcon;
    }
  };

  const rolledUpActivities = useMemo(() => {
    if (!activities || activities.length === 0) {
      return [] as Activity[];
    }

    const sortedActivities = [...activities].sort(
      (a, b) => getActivityTime(b) - getActivityTime(a)
    );

    const buckets: Array<{
      key: string | null;
      anchorTime: number;
      entries: Activity[];
    }> = [];

    sortedActivities.forEach((activity) => {
      const key = getRollupKey(activity);
      const activityTime = getActivityTime(activity);
      const lastBucket = buckets[buckets.length - 1];

      if (
        lastBucket &&
        key &&
        lastBucket.key === key &&
        lastBucket.anchorTime - activityTime <= ROLLUP_WINDOW_MS
      ) {
        lastBucket.entries.push(activity);
        return;
      }

      buckets.push({
        key,
        anchorTime: activityTime,
        entries: [activity],
      });
    });

    return buckets.map((bucket) => {
      const [firstEntry] = bucket.entries;
      if (!firstEntry || bucket.entries.length === 1 || !bucket.key) {
        return firstEntry;
      }

      const questionKeys = new Set<string>();
      bucket.entries.forEach((entry, index) => {
        if (typeof entry.metadata.questionId === 'number') {
          questionKeys.add(`id:${entry.metadata.questionId}`);
        } else if (typeof entry.metadata.questionLabel === 'string') {
          questionKeys.add(`label:${entry.metadata.questionLabel}`);
        } else {
          questionKeys.add(`entry:${index}`);
        }
      });

      const questionCount = questionKeys.size;
      const userName =
        typeof firstEntry.metadata.userName === 'string'
          ? firstEntry.metadata.userName
          : 'A user';
      const assessmentLabel =
        typeof firstEntry.metadata.assessmentId === 'number'
          ? `evaluation #${firstEntry.metadata.assessmentId}`
          : 'an evaluation';
      const questionNoun = questionCount === 1 ? 'question' : 'questions';

      return {
        ...firstEntry,
        type: 'response_updated',
        description: `${userName} updated ${questionCount} ${questionNoun} on ${assessmentLabel}`,
        metadata: {
          ...firstEntry.metadata,
          groupedCount: bucket.entries.length,
          groupedQuestionCount: questionCount,
        },
      };
    }).filter((activity): activity is Activity => Boolean(activity)).slice(0, 5);
  }, [activities]);

  const hasCreateClientPermission =
    user?.permissions?.some(
      (p) => p === 'clients:write' || p === 'admin:full'
    ) ?? false;

  if (clientsLoading || statsLoading || usersStatsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Overview of your agency evaluations and system activity
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {visibleStats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="card hover:shadow-lg transition-shadow duration-200"
          >
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`p-2 rounded-md ${stat.color}`}>
                    <stat.icon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="text-base font-medium text-gray-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Recent Clients - Only for non-restricted-clients */}
        {!isRestrictedClient && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-base font-medium text-gray-900">Recent Clients</h3>
              <Link
                to="/clients"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                View all
              </Link>
            </div>
            <div className="card-body">
              {clientsData?.clients.length ? (
                <div className="space-y-3">
                  {clientsData.clients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {client.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {client.industry || 'No industry specified'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className={`badge ${client.status === 'active' ? 'badge-success' : 'badge-gray'
                          }`}>
                          {client.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <BuildingOfficeIcon className="mx-auto h-10 w-10 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No clients</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Get started by creating a new client.
                  </p>
                  <div className="mt-4">
                    <Link
                      to="/clients"
                      className="btn btn-primary"
                    >
                      Add Client
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-base font-medium text-gray-900">Recent Activity</h3>
          </div>
          <div className="card-body">
            {activitiesLoading ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : rolledUpActivities.length > 0 ? (
              <div className="flow-root">
                <ul className="-mb-6">
                  {rolledUpActivities.map((activity, activityIdx) => {
                    const ActivityIcon = getActivityIcon(activity.type);
                    const groupedCount =
                      typeof activity.metadata.groupedCount === 'number'
                        ? activity.metadata.groupedCount
                        : 1;
                    return (
                      <li key={activity.id}>
                        <div className="relative pb-6">
                          {activityIdx !== rolledUpActivities.length - 1 ? (
                            <span
                              className="absolute top-3 left-3 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-2">
                            <div>
                              <span className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center ring-4 ring-white">
                                <ActivityIcon className="h-4 w-4 text-gray-500" />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  {activity.description}
                                  {groupedCount > 1 ? (
                                    <span className="ml-2 inline-flex items-center rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                                      {groupedCount} updates
                                    </span>
                                  ) : null}
                                </p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                <time>{formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}</time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center py-4">
                <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Activity will appear here as evaluations are created and completed.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-base font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {hasCreateClientPermission && (
              <Link
                to="/clients"
                className="relative rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
              >
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="absolute inset-0" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-900">Add Client</p>
                  <p className="text-xs text-gray-500">Create new client profile</p>
                </div>
              </Link>
            )}

            <Link
              to="/assessments"
              className="relative rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
            >
              <div className="flex-shrink-0">
                <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">Start Evaluation</p>
                <p className="text-xs text-gray-500">Begin new maturity evaluation</p>
              </div>
            </Link>

            {/* Share / Invite Client - Hide for restricted clients */}
            {!isRestrictedClient && (
              <Link
                to="/clients" // Or wherever invite logic lives if direct
                className="relative rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
              >
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="absolute inset-0" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-900">Share / Invite Client</p>
                  <p className="text-xs text-gray-500">Give client access</p>
                </div>
              </Link>
            )}

            {!isRestrictedClient && (
              <Link
                to="/clients"
                className="relative rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
              >
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="absolute inset-0" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-900">Manage Users</p>
                  <p className="text-xs text-gray-500">Invite client users</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
